import { useState, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { getLoggedinUser } from "@/store/slices/auth/authSlice";
import { LeadModel } from "@blazeo.com/calendar-client";
import { getAllRequests } from "@/store/slices/requests";
import useSignalRClient from "@/services/signal-R/signalr-hubs";
import OnInitHub from "@/services/signal-R/on-receive-hook";
import Appointment_signalr from "../../../appointment-signalr-constants";
import { toast } from "react-toastify";
import { success, toastMsg } from "@/services/variables";
import UseGql from "@/services/graph-ql/useGql";
import { Get_Appointments_By_Lead_Id } from "@/modules/crm/leads/graphql-queries/appointment-scheduling";
import {
  getAppointments,
  getCalendars,
  getServiceTypes,
  getTempStatusPayload,
  setAppointmentsToState,
  setTempStatusPayload,
} from "@/store/slices/appointment/appointmentSlice";

import moment from "moment";
import { useRouter } from "next/router";
import { Enums, AttendeeStatus, FeatureKeys } from "@/services/dropdown-enums";
import { cloneDeep } from "@/services/generic.methods";
import useManageLead from "@/hooks/leads/useManageLead";
import useFeatures from "@/hooks/useFeatures";
import { getTimezones } from "@/store/slices/general/generalSlice";
import { makeRangeQuery } from "../../main-page/detail-section";
import { isCanceledAppointment } from "../../main-page/helper";
import { Get_Appointments } from "../../../graphql-queries";
import { useCRMDataClient } from "@/services/data-client/DataClientContext";
import {
  enrichAppointmentsWithFlow,
  getBlazeoLeadGuid,
} from "@/modules/crm/leads/components/lead-details/right-section/appointment/routing-helpers";
import { mergePinnedLeadAppointments } from "@/modules/crm/leads/components/lead-details/right-section/pinnedLeadAppointments";

const APPOINTMENT_ACTION_SUCCESS = {
  createAppointment: "created",
  updateAppointment: "updated",
  rescheduleAppointment: "rescheduled",
  completeAppointment: "completed",
  cancelAppointment: "canceled",
  deleteAppointment: "deleted",
};

const appointmentIds = (appointment) =>
  [appointment?.id, appointment?.eventId]
    .filter((id) => id != null && id !== "")
    .map((id) => String(id));

const isSameAppointment = (appointment, ...candidates) => {
  const ids = appointmentIds(appointment);
  const targets = candidates
    .flatMap((candidate) =>
      candidate && typeof candidate === "object"
        ? appointmentIds(candidate)
        : candidate != null && candidate !== ""
          ? [String(candidate)]
          : []
    );
  return ids.some((id) => targets.includes(id));
};

const buildSubmittedAppointmentPatch = (value = {}) => {
  const patch = {};

  if (value.title !== undefined) patch.title = value.title;
  if (value.notes !== undefined) {
    patch.notes = value.notes;
    patch.description = value.notes;
  } else if (value.description !== undefined) {
    patch.description = value.description;
    patch.notes = value.description;
  }
  if (value.visitorName !== undefined) patch.visitorName = value.visitorName;
  if (value.visitorEmail !== undefined || value.email !== undefined) {
    const email = value.visitorEmail || value.email || "";
    patch.visitorEmail = email;
    patch.email = email;
  }
  if (value.visitorPhone !== undefined || value.phone !== undefined) {
    const phone = value.visitorPhone || value.phone || "";
    patch.visitorPhone = phone;
    patch.phone = phone;
  }
  if (value.visitorName !== undefined || value.name !== undefined) {
    patch.name = value.name || value.visitorName;
  }
  if (value.timeZone !== undefined) patch.timeZone = value.timeZone;
  if (value.leadId !== undefined) patch.leadId = value.leadId;
  if (value.memberId !== undefined) patch.memberId = value.memberId;
  if (value.participantId !== undefined || value.memberId !== undefined) {
    patch.participantId = value.participantId || value.memberId;
  }
  if (value.calendarId !== undefined) patch.calendarId = value.calendarId;

  if (
    value.calendarLocationId !== undefined ||
    value.customLocation !== undefined
  ) {
    const isCustom = Number(value.calendarLocationId) === 3;
    if (isCustom) {
      patch.calendarLocationId = null;
      patch.customLocation = value.customLocation ?? "";
    } else {
      patch.calendarLocationId = value.calendarLocationId ?? null;
      patch.customLocation = null;
    }
  }

  return patch;
};

const findCalendarLocation = (calendars, calendarId, calendarLocationId) => {
  if (calendarLocationId == null || calendarLocationId === "") return null;
  const calendar = (calendars || []).find((item) =>
    [item?.calendarId, item?.uuid, item?.id].some(
      (id) => id != null && String(id) === String(calendarId)
    )
  );
  return (calendar?.appointmentLocations || []).find(
    (location) =>
      String(location.calendarLocationId || location.id) ===
      String(calendarLocationId)
  );
};

const useManageAppointment = (config = {}) => {
  const {
    showResponse = true,
    showStatusResponse = false,
    leadById = false,
    lead: configLead = null,
    callback = () => {},
  } = config;
  const getAppointmentsGQL = UseGql(Get_Appointments);
  const getAppointmentsByLeadIdGQL = UseGql(Get_Appointments_By_Lead_Id);
  const router = useRouter();
  const { id } = router.query;
  const [isDeleteRequest, setIsDeleteRequest] = useState(false);
  const [appointmentIdToBeDeleted, setAppointmentIdToBeDeleted] = useState(-1);
  const appointmentsState = useSelector(getAppointments);
  const calendarsState = useSelector(getCalendars);
  const serviceTypesState = useSelector(getServiceTypes);
  const tempStatusPayload = useSelector(getTempStatusPayload);
  const timezonesList = useSelector(getTimezones);
  const [isChanging, setIsChanging] = useState(false);

  useEffect(() => {
    if (id && leadById && configLead) {
      fetchAppointmentsByLeadId(id, configLead);
    }
  }, [id, configLead?.email, configLead?.phone, configLead?.countryCode]);

  const dispatch = useDispatch();
  const {
    hubName,
    InvokeMethodNames: {
      appointmentBooking,
      appointmentReschedule,
      appointmentUpdate,
      appointmentCancellation,
      appointmentDelete,
      appointmentComplete,
    },
    onReceiveMethodNames: {
      onAppointmentBookingResponse,
      onAppointmentRescheduleResponse,
      onAppointmentUpdateResponse,
      onAppointmentCancellationResponse,
      onAppointmentDeleteResponse,
      onAppointmentCompleteResponse,
    },
  } = Appointment_signalr;

  const resultMethods = {
    [onAppointmentBookingResponse]: { successOperation: "created" },
    [onAppointmentRescheduleResponse]: { successOperation: "rescheduled" },
    [onAppointmentUpdateResponse]: { successOperation: "updated" },
  };
  const statusResultMethods = {
    [onAppointmentCancellationResponse]: { successOperation: "canceled" },
    [onAppointmentDeleteResponse]: { successOperation: "deleted" },
    [onAppointmentCompleteResponse]: { successOperation: "completed" },
  };

  const methodKeys = Object.keys(resultMethods);
  const statusMethodKeys = Object.keys(statusResultMethods);

  const { statuses, RTC } = useSignalRClient({ hubName: hubName });
  const appointmentHub = RTC[hubName];
  const requests = useSelector(getAllRequests);
  const { clearStateByMethods } = OnInitHub();
  const [loading, setLoading] = useState(false);
  const [submittingCB, setSubmittingCB] = useState(null);
  const timezones = useSelector(getTimezones);

  // refetch lead worflows on appt schedule save/update response
  const { getLeadWorkflows } = useManageLead({});
  const { client: dataClient } = useCRMDataClient();
  const { hasFeature } = useFeatures();
  const hasCRM = hasFeature(FeatureKeys.CRM);
  const user = useSelector(getLoggedinUser);
  const blazeoLeadIdRef = useRef(null);

  const handleResponse = (methodKeys, resultMethods) => {
    let findedKey = methodKeys.find((key) => key in requests && requests[key]);
    // if(findedKey == onAppointmentCancellationResponse && isDeleteRequest && appointmentIdToBeDeleted > -1) {
    //   handleAppointmentStatus(appointmentIdToBeDeleted);
    //   setIsDeleteRequest(false)
    //   setAppointmentIdToBeDeleted(-1)
    //   clearStateByMethods(methodKeys);
    //   return
    // }
    if (findedKey) {
      let response = requests[findedKey];
      let successOperation = resultMethods[findedKey]?.successOperation;
      responseHandler(response, successOperation, findedKey);
      clearStateByMethods(methodKeys);
    }
  };
  useEffect(() => {
    if (config?.fetchCalendars) {
      config?.fetchCalendars();
    }

    if (config?.fetchAppointments) {
      config?.fetchAppointments(
        makeRangeQuery(config?.range, config?.selectedView)
      );
    }
  }, []);

  const responseHandler = (response, successOperation = "", methodName) => {
    setLoading(false);
    if (response.isSuccess) {
      setIsChanging(true);
      setTimeout(() => {
        setIsChanging(false);
      });
      if (tempStatusPayload) {
        let allAppointments = cloneDeep(appointmentsState);
        if (tempStatusPayload?.type == "delete") {
          allAppointments = allAppointments.filter(
            (appointment) => appointment.id != tempStatusPayload?.appointmentId
          );
        } else {
          allAppointments = allAppointments.map((appointment) => {
            if (appointment.id == tempStatusPayload?.appointmentId) {
              appointment.status = tempStatusPayload?.type;
            }
            return appointment;
          });
        }
        dispatch(setAppointmentsToState(allAppointments));
      }
      onSuccess(successOperation);
    } else {
      let msg = response.errors[0];
      if (!msg && methodName == onAppointmentCancellationResponse) {
        msg = "Cannot cancel. The allowed cancellation window has closed";
      }
      onError(msg);
    }
    dispatch(setTempStatusPayload(null));
  };

  const onSuccess = (operation) => {
    toast.success(success.saveSuccess("Appointment", operation));

    if (id) {
      getLeadWorkflows(+id);
    }
  };

  const onError = (errorMsg) => {
    if (errorMsg) {
      toast.error(toastMsg.errors.customError(errorMsg));
      console.log("Error in response::", errorMsg);
    }
  };

  const getSaveMethod = (data, requestType) => {
    if (!requestType) {
      return appointmentBooking;
    } else if (requestType === "update") {
      return appointmentUpdate;
    } else if (requestType === "reschedule") {
      return appointmentReschedule;
    }
  };

  const [offsetMinutes, setOffsetMinutes] = useState(0);

  const updateOffsetFromTimezone = (timezoneValue) => {
    const tz = timezonesList.find((t) => t.value == timezoneValue);
    if (tz) {
      const offsetStr = getOffset(tz.label);
      if (offsetStr) {
        const sign = offsetStr.startsWith("-") ? -1 : 1;
        const [hours, minutes] = offsetStr
          .replace(/[+-]/, "")
          .split(":")
          .map(Number);
        const totalMinutes = sign * (hours * 60 + minutes);
        setOffsetMinutes(totalMinutes);
      }
    }
  };

  const getOffset = (timezone) => {
    const regex = /\(UTC([+-]\d{2}:\d{2})\)/;
    const result = regex.exec(timezone);

    if (result) {
      const matchedOutput = result[1]; // This will give you just "+05:00"
      return matchedOutput;
    } else {
      console.log("No match found");
    }
  };

  const handleFormSubmit = (
    AppointmentForm,
    value,
    data,
    requestType,
    cb = () => {},
    lead = {}
  ) => {
    const payload = createPayload(value, lead);

    const emailToValidate = payload.visitorEmail || payload.email;
    if (!emailToValidate || !emailToValidate.trim()) {
      toast.error(toastMsg.errors.customError("Visitor email is required"));
      if (config?.handleModalClose) config.handleModalClose(false);
      if (typeof cb === "function") cb(false);
      return;
    }

    setLoading(true);

    const action = !requestType
      ? "createAppointment"
      : requestType === "update"
        ? "updateAppointment"
        : requestType === "reschedule"
          ? "rescheduleAppointment"
          : "createAppointment";

    dataClient.appointments[action](payload)
      .then((res) => {
        console.log(`Appointment ${action} callback`, res);
        setLoading(false);
        toast.success(
          success.custom(
            `Appointment ${APPOINTMENT_ACTION_SUCCESS[action]} successfully!`
          )
        );
        const mappedEvent = res?.event
          ? dataClient.appointments._mapAppointmentItem(res.event)
          : null;
        let eventForDispatch = mappedEvent;

        if (action === "createAppointment" && mappedEvent) {
          const allAppointments = cloneDeep(appointmentsState) || [];
          allAppointments.unshift(mappedEvent);
          dispatch(setAppointmentsToState(allAppointments));
        } else if (action !== "createAppointment") {
          const submittedPatch = buildSubmittedAppointmentPatch(value);
          // API + form use wall-clock datetimes (e.g. 2026-06-23T20:00:00), not true UTC ISO.
          const finalStartDate =
            value?.startDate || mappedEvent?.startDate || data?.startDate;
          const finalEndDate =
            value?.endDate || mappedEvent?.endDate || data?.endDate;
          const locationCalendarId =
            submittedPatch.calendarId ||
            value?.calendarId ||
            data?.calendarId ||
            mappedEvent?.calendarId;
          const savedLocation = findCalendarLocation(
            [...(calendarsState || []), ...(serviceTypesState || [])],
            locationCalendarId,
            submittedPatch.calendarLocationId
          );
          const locationSource = submittedPatch.customLocation
            ? { customLocation: submittedPatch.customLocation }
            : savedLocation
              ? {
                  calendarLocation: {
                    type: savedLocation.type,
                    value: savedLocation.value || savedLocation.name,
                  },
                }
              : null;
          const locationInfo = locationSource
            ? dataClient.appointments._resolveLocationInfo(locationSource)
            : null;

          let allAppointments = cloneDeep(appointmentsState) || [];
          allAppointments = allAppointments.map((appt) => {
            if (!isSameAppointment(appt, data, value, mappedEvent)) {
              return appt;
            }
            const cleanMappedEvent = {};
            if (mappedEvent) {
              Object.keys(mappedEvent).forEach((key) => {
                if (
                  mappedEvent[key] !== null &&
                  mappedEvent[key] !== undefined &&
                  mappedEvent[key] !== ""
                ) {
                  cleanMappedEvent[key] = mappedEvent[key];
                }
              });
            }
            delete cleanMappedEvent.startDate;
            delete cleanMappedEvent.endDate;
            delete cleanMappedEvent.start;
            delete cleanMappedEvent.end;
            delete cleanMappedEvent.calendarLocationId;
            delete cleanMappedEvent.customLocation;
            delete cleanMappedEvent.locationText;
            delete cleanMappedEvent.locationIcon;
            delete cleanMappedEvent.locationLink;

            const merged = {
              ...appt,
              ...cleanMappedEvent,
              ...submittedPatch,
              startDate: finalStartDate,
              endDate: finalEndDate,
              start: finalStartDate
                ? moment(finalStartDate).toDate()
                : appt.start,
              end: finalEndDate ? moment(finalEndDate).toDate() : appt.end,
              timeZone:
                submittedPatch.timeZone ||
                appt.timeZone ||
                mappedEvent?.timeZone ||
                value.timeZone,
              status:
                action === "rescheduleAppointment"
                  ? Enums.AppointmentStates.ReScheduled
                  : (cleanMappedEvent.status ?? appt.status),
              id: appt.id,
              eventId: appt.eventId || mappedEvent?.eventId,
              ...(locationInfo
                ? {
                    locationText: locationInfo.text,
                    locationIcon: locationInfo.icon,
                    locationLink: locationInfo.link,
                  }
                : {}),
              ...(savedLocation
                ? {
                    calendarLocation: {
                      type: savedLocation.type,
                      value: savedLocation.value || savedLocation.name,
                    },
                  }
                : submittedPatch.customLocation
                  ? { calendarLocation: null }
                  : {}),
            };
            eventForDispatch = merged;
            return merged;
          });

          allAppointments.sort(
            (a, b) => new Date(a.startDate) - new Date(b.startDate)
          );
          dispatch(setAppointmentsToState(allAppointments));
        }

        if (eventForDispatch) {
          window.dispatchEvent(
            new CustomEvent("appointment-created", { detail: eventForDispatch })
          );
        }

        if (typeof cb === "function") cb(true, res);
        if (config?.handleModalClose) config.handleModalClose(false);
        getLeadWorkflows();
      })
      .catch((err) => {
        setLoading(false);
        onError(err.message);
      });
  };
  const createPayload = (data, lead) => {
    const payload = {};
    const omitKeys = [
      "guest",
      "memberId",
      "organizer",
      "serviceType",
      "start",
      "end",
      "selectedTimeZone",
      "serviceTypeId",
      "userId",
      "color",
      "__typename",
      "type",
    ];

    Object.keys(data || {}).forEach((item) => {
      if (!omitKeys.includes(item)) {
        payload[item] = data[item];
      }
    });

    payload.offsetMinutes = offsetMinutes;
    if (data?.notes) {
      payload.notes = data.notes;
    }

    // Handle Location Selection
    if (data.calendarLocationId === 3) {
      if (data.customLocation !== undefined) {
        payload.customLocation = data.customLocation;
      }
    } else {
      if (data.calendarLocationId !== undefined) {
        payload.calendarLocationId = data.calendarLocationId;
      }
    }

    if (payload.calendarId) {
      payload.thirdPartyCalendarId = payload.calendarId;
    }

    if (lead) {
      payload.leadId =
        getBlazeoLeadGuid(lead.leadId, lead.LeadId, blazeoLeadIdRef.current) ||
        (hasCRM ? undefined : lead?.id || lead?.leadId || payload?.leadId);
      payload.visitorName =
        lead?.firstName && lead?.lastName
          ? `${lead.firstName} ${lead.lastName}`
          : lead?.firstName || lead?.lastName || lead?.name;
      payload.visitorEmail =
        lead?.email || data?.visitorEmail || data?.email || "";
      payload.email = lead?.email || data?.email || "";
      payload.visitorPhone =
        lead?.countryCode && lead?.phone
          ? `${lead.countryCode} ${lead.phone}`
          : lead?.phone || "";
    }

    if (payload.startDate) {
      const start = moment.utc(payload.startDate);
      payload.startHour = start.hour();
      payload.startMinute = start.minute();
      payload.startDate = start.format("YYYY-MM-DDTHH:mm:ss");
    }

    if (payload.endDate) {
      const end = moment.utc(payload.endDate);
      payload.endHour = end.hour();
      payload.endMinute = end.minute();
      payload.endDate = end.format("YYYY-MM-DDTHH:mm:ss");
    }

    return payload;
  };

  // const fetchAppointments = async () => {
  //   dispatch(setAppointmentsToState([]))
  //   try {
  //     return getAppointmentsGQL.executeQuery().then(data => {
  //       const response = data?.data?.appointments?.items
  //       if (response?.length) {
  //         dispatch(setAppointmentsToState(response))
  //       } else {
  //         dispatch(setAppointmentsToState([]))
  //       }
  //     })

  //   } catch (error) {
  //     console.error("Fetch appointment error::", error)
  //   }
  // }
  const fetchAppointments = async (variables = {}) => {
    dispatch(setAppointmentsToState([]));
    try {
      // Pass `query` for Pro plan GQL routing.
      // Basic plan ignores `query` and uses _extractFilters on `where`.
      const response = await dataClient.appointments.getAppointments({
        ...variables,
        query: Get_Appointments,
      });
      const appointments = response?.appointments?.items || [];
      dispatch(setAppointmentsToState(appointments));
      if (config?.setColors) {
        config.setColors(appointments);
      }

      enrichAppointmentsWithFlow(dataClient, appointments).then((enriched) => {
        dispatch(setAppointmentsToState(enriched));
        if (config?.setColors) {
          config.setColors(enriched);
        }
      });
    } catch (error) {
      console.error("Fetch appointment error:", error);
      dispatch(setAppointmentsToState([]));
    }
  };

  const resolveLiveBlazeoLeadId = async (lead) => {
    const existing = getBlazeoLeadGuid(lead?.leadId, lead?.LeadId);
    if (existing) return existing;
    const email = lead?.email;
    const companyKey = user?.CompanyKey || user?.companyKey;
    if (!email || !companyKey) return null;
    const blazeoLead = await LeadModel.getByEmail(email, companyKey);
    if (!blazeoLead || blazeoLead.isDeleted) return null;
    return getBlazeoLeadGuid(blazeoLead.leadId, blazeoLead.LeadId);
  };

  const fetchAppointmentsByLeadId = async (
    leadId,
    lead = null,
    clear = true
  ) => {
    if (clear) dispatch(setAppointmentsToState([]));
    try {
      const fetchParams = { query: Get_Appointments_By_Lead_Id };

      const blazeoLeadId =
        getBlazeoLeadGuid(leadId, lead?.leadId, lead?.LeadId, blazeoLeadIdRef.current) ||
        (await resolveLiveBlazeoLeadId(lead));
      blazeoLeadIdRef.current = blazeoLeadId;
      if (!blazeoLeadId) {
        dispatch(setAppointmentsToState([]));
        return;
      }
      fetchParams.leadId = blazeoLeadId;

      const response =
        await dataClient.appointments.getAppointments(fetchParams);
      const appointments = response?.appointments?.items || [];
      const enriched = await enrichAppointmentsWithFlow(
        dataClient,
        appointments
      );
      dispatch(
        setAppointmentsToState(
          mergePinnedLeadAppointments(enriched, blazeoLeadId)
        )
      );
    } catch (error) {
      console.error("Fetch appointments by lead error:", error);
      dispatch(setAppointmentsToState([]));
    }
  };

  const mapAppointments = (appointments, filterType = false, excludeBusy = false, lead = null) => {
    const isUpcoming = filterType === true || filterType === "active";
    const sourceAppointments = isUpcoming
      ? [...(appointments || [])].sort((a, b) => {
          const startA = moment.utc(a?.startDate).valueOf();
          const startB = moment.utc(b?.startDate).valueOf();
          return (Number.isFinite(startA) ? startA : 0) - (Number.isFinite(startB) ? startB : 0);
        })
      : appointments || [];
    const mappedAppointments = sourceAppointments.reduce((acc, appointment) => {
      // Skip busy slots when excludeBusy is true (for lead details view)
      if (excludeBusy && appointment.isBusy) {
        return acc;
      }

      if (
        (filterType === true || filterType === "active") &&
        isCanceledAppointment(appointment)
      ) {
        return acc;
      }

      // Filter by the live Blazeo lead guid when CRM reused an email after a delete.
      if (lead) {
        const blazeoLeadId = blazeoLeadIdRef.current;
        if (blazeoLeadId) {
          const appointmentLeadId = getBlazeoLeadGuid(
            appointment.leadId,
            appointment.LeadId
          );
          if (
            appointmentLeadId &&
            appointmentLeadId.toLowerCase() !== blazeoLeadId.toLowerCase()
          ) {
            return acc;
          }
        } else {
          const leadPhone =
            lead?.countryCode && lead?.phone
              ? `${lead.countryCode} ${lead.phone}`
              : lead?.phone;
          const appointmentPhone = appointment?.visitorPhone;

          const leadEmail = lead?.email;
          const appointmentEmail =
            appointment?.visitorEmail || appointment?.email;

          const isMatch =
            (leadEmail &&
              appointmentEmail &&
              leadEmail.toLowerCase() === appointmentEmail.toLowerCase()) ||
            (leadPhone && appointmentPhone && leadPhone === appointmentPhone);

          if (!isMatch) {
            return acc;
          }
        }
      }

      const startDate = moment.utc(appointment.startDate); //.tz(appointment.timeZone);
      const endDate = moment.utc(appointment.endDate); //.tz(appointment.timeZone);
      const dateKey = startDate.format("DD-MM-YYYY");

      const format = "MM/DD/YY HH:mm:ss";
      const today = moment().format(format);
      const endDateTime = moment.utc(endDate).format(format);

      const todayMoment = moment(today, format);
      const endDateTimeMoment = moment(endDateTime, format);
      const isBefore = endDateTimeMoment.isBefore(todayMoment);

      if (filterType === true || filterType === "active") {
        if (isBefore) {
          return acc;
        }
      } else if (filterType === "past" || filterType === "history") {
        if (!isBefore) {
          return acc;
        }
      }

      if (!acc[dateKey]) {
        acc[dateKey] = [];
      }

      acc[dateKey].push({
        name: appointment.title,
        ...appointment,
        startTime: startDate.format("h:mm A"),
        endTime: endDate.format("h:mm A"),
        date: startDate.format("ddd, MMMM D"),
        user: {
          name: appointment.guest,
        },
      });

      return acc;
    }, {});
    return mappedAppointments;
  };

  const getMethodName = (appointmentId, type) => {
    let methodName = appointmentDelete;
    if (type == Enums.AppointmentStates.Completed) {
      methodName = appointmentComplete;
    } else if (type == Enums.AppointmentStates.Canceled) {
      methodName = appointmentCancellation;
    } else if (type == Enums.AppointmentStates.Deleted) {
      methodName = appointmentDelete;
      // methodName = appointmentCancellation; // Cancel before deletion
      // setIsDeleteRequest(true);
      // setAppointmentIdToBeDeleted(appointmentId)
      // setTimeout(() => {
      // handleAppointmentStatus(appointmentId);
      // }, 200)
    }
    return methodName;
  };
  const handleAppointmentStatus = async (appointmentId, type) => {
    if (type === "reschedule" || type === "update") return;

    setLoading(true);
    const action =
      type === "delete" || type === Enums.AppointmentStates.Deleted
        ? "deleteAppointment"
        : type === "complete" || type === AttendeeStatus.None
          ? "completeAppointment"
          : "cancelAppointment";

    return dataClient.appointments[action](appointmentId)
      .then((res) => {
        console.log(`Appointment ${action} callback`, res);
        setLoading(false);
        toast.success(
          success.custom(
            `Appointment ${APPOINTMENT_ACTION_SUCCESS[action]} successfully!`
          )
        );

        dispatch(setTempStatusPayload({ type, appointmentId }));

        // Update local state if needed
        let allAppointments = cloneDeep(appointmentsState);
        if (action === "deleteAppointment") {
          allAppointments = allAppointments.filter(
            (appt) => appt.id != appointmentId
          );
        } else {
          const nextStatus =
            action === "completeAppointment"
              ? Enums.AppointmentStates.Completed
              : Enums.AppointmentStates.Canceled;
          allAppointments = allAppointments.map((appt) => {
            if (appt.id == appointmentId) appt.status = nextStatus;
            return appt;
          });
        }
        dispatch(setAppointmentsToState(allAppointments));
        if (typeof callback === "function") callback(true, res);
        window.dispatchEvent(
          new CustomEvent("appointment-deleted", {
            detail: { appointmentId, type, action },
          })
        );

        return true;
      })
      .catch((err) => {
        setLoading(false);
        onError(err.message);
        return false;
      });
  };

  return {
    loading,
    handleFormSubmit,
    fetchAppointments,
    fetchAppointmentsByLeadId,
    mapAppointments,
    handleAppointmentStatus,
    setLoading,
    isChanging,
    setIsChanging,
    offsetMinutes,
    updateOffsetFromTimezone,
  };
};
export default useManageAppointment;
